"use client";

import { ChevronDown } from "lucide-react";
import { useState } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function FrequentlyAskedQuestions() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1,
    once: true
  });
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const faqs = [{
    id: 1,
    question: "What is an Agentic Artist?",
    answer: "An Agentic Artist designs AI-driven automation systems that eliminate administrative friction. We build custom solutions for dance studios and nonprofits, allowing you to reclaim time and focus on creativity instead of paperwork."
  }, {
    id: 2,
    question: "Who benefits most from your services?",
    answer: "Studio owners and nonprofit directors who are overwhelmed by repetitive admin tasks. If you're spending hours on scheduling, billing, communications, or data entry, our automation systems can transform your workflow."
  }, {
    id: 3,
    question: "How long does an automation system take to implement?",
    answer: "Most systems are designed and deployed within 4-6 weeks, depending on complexity. We start with a Strategy Audit to identify your biggest administrative leaks, then build a custom solution that integrates seamlessly with your existing tools."
  }, {
    id: 4,
    question: "Do I need technical expertise to use your systems?",
    answer: "No. Our AI-driven systems are built to be intuitive and user-friendly. We provide comprehensive training and ongoing support to ensure your team can operate the automation with confidence."
  }, {
    id: 5,
    question: "What makes you different from other consultants?",
    answer: "We blend cybersecurity-level precision with artistic creativity. As a professional Hip-Hop choreographer and analyst, I understand both the technical rigor needed for secure automation and the rhythmic flow of creative work. This dual perspective creates solutions that are efficient, secure, and human-centered."
  }, {
    id: 6,
    question: "How do I get started?",
    answer: "Book a Strategy Audit to identify your Administrative Leak. We'll analyze your current workflow, pinpoint inefficiencies, and propose a custom automation architecture. From there, we design, build, and deploy your system with full training and support."
  }];
  const toggleFaq = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };
  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      toggleFaq(index);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      const nextIndex = (index + 1) % faqs.length;
      document.getElementById(`faq-trigger-${nextIndex}`)?.focus();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      const prevIndex = (index - 1 + faqs.length) % faqs.length;
      document.getElementById(`faq-trigger-${prevIndex}`)?.focus();
    }
  };
  return <section ref={ref} className="relative bg-dark-background py-16 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-4xl">
        <div className={`mb-12 text-center transition-all duration-700 ${isVisible ? "translate-y-0 opacity-100" : "translate-y-5 opacity-0"}`}>
          <h2 className="mb-4 font-display text-4xl font-bold text-foreground-dark sm:text-5xl">
            Frequently Asked Questions
          </h2>
          <div className="mx-auto h-1 w-24 bg-primary"></div>
        </div>

        <div className={`space-y-4 transition-all duration-700 delay-200 ${isVisible ? "translate-y-0 opacity-100" : "translate-y-5 opacity-0"}`}>
          {faqs.map((faq, i) => <div key={faq.id} data-index={i} className="group overflow-hidden rounded-lg border border-primary/20 bg-muted-dark-background transition-all duration-300 hover:border-primary/40">
              <button id={`faq-trigger-${i}`} onClick={() => toggleFaq(i)} onKeyDown={e => handleKeyDown(e, i)} className="flex w-full items-start justify-between gap-4 px-6 py-5 text-left transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary" aria-expanded={openIndex === i} aria-controls={`faq-content-${i}`}>
                <span className="font-display text-lg font-semibold text-foreground-dark transition-colors duration-200 group-hover:text-primary group-focus:text-primary">
                  {faq.question}
                </span>
                <ChevronDown className={`mt-1 size-5 shrink-0 text-primary transition-transform duration-300 ${openIndex === i ? "rotate-90" : ""}`} aria-hidden="true" />
              </button>

              <div id={`faq-content-${i}`} className={`grid transition-all duration-300 ease-in-out ${openIndex === i ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`} aria-hidden={openIndex !== i}>
                <div className="overflow-hidden">
                  <div className="border-t border-primary/10 px-6 py-5 text-foreground-muted-dark">
                    {faq.answer}
                  </div>
                </div>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}